import React, { useEffect, useState } from 'react';
import { ui } from '../ui';
import { Agent } from '../api';
import { CallsDateRangePreset, CALLS_DATE_RANGE_PRESETS } from '../dateRange';

// How long to wait after the Account field stops changing before applying it (FR-2.3 — a
// short debounce so partial typing progressively narrows results, rather than requiring a
// blur/Enter before anything happens).
const ACCOUNT_DEBOUNCE_MS = 400;

export interface AllCallsFilterBarProps {
  dateRangePreset: CallsDateRangePreset;
  onDateRangePresetChange: (preset: CallsDateRangePreset) => void;
  customFrom: string;
  onCustomFromChange: (value: string) => void;
  customTo: string;
  onCustomToChange: (value: string) => void;
  // '' = All Agents, 'unassigned' = Unassigned (maps to unassignedOnly), else String(AgentID).
  agentSelection: string;
  onAgentSelectionChange: (value: string) => void;
  agents: Agent[];
  // The applied (debounced) value — see the internal accountText state below for what the
  // input actually displays while the admin is mid-type.
  accountNumber: string;
  onAccountNumberChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  statusOptions: string[];
  onClearFilters: () => void;
}

// All Calls card's filter bar (docs/requirements/admin-calls-filters-and-transcript.md, FR
// Group 2 / OQ-5) — Date Range preset+custom, Agent, Account, and Status, each with a real
// `<label htmlFor>` (FR-2.7), plus a Clear filters action (FR-2.6). Extracted out of
// AdminPage.tsx as its own component since it carries its own nontrivial state (the Account
// field's debounce), mirroring TranscriptRetrievalPanel.tsx/TimeTrackingAdmin.tsx/
// ZoomSweepPanel.tsx's existing self-contained-section precedent.
export const AllCallsFilterBar: React.FC<AllCallsFilterBarProps> = ({
  dateRangePreset, onDateRangePresetChange,
  customFrom, onCustomFromChange,
  customTo, onCustomToChange,
  agentSelection, onAgentSelectionChange, agents,
  accountNumber, onAccountNumberChange,
  status, onStatusChange, statusOptions,
  onClearFilters,
}) => {
  // Local, immediately-responsive text for the Account input. The parent's `accountNumber`
  // prop is only updated once the debounce below settles — that's the value that actually
  // drives the fetch — and this local state re-syncs from the prop whenever the parent resets
  // it from elsewhere (e.g. Clear filters), so the field never shows a stale typed value.
  const [accountText, setAccountText] = useState(accountNumber);
  useEffect(() => {
    setAccountText(accountNumber);
  }, [accountNumber]);
  useEffect(() => {
    const trimmed = accountText.trim();
    if (trimmed === accountNumber) return undefined;
    const timer = setTimeout(() => onAccountNumberChange(trimmed), ACCOUNT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountText]);

  const sortedAgents = [...agents].sort((a, b) =>
    (a.DisplayName ?? a.AzureADObjectID ?? '').localeCompare(b.DisplayName ?? b.AzureADObjectID ?? ''));

  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      <div>
        <label className={ui.label} htmlFor="calls-filter-date-range">Date range</label>
        <select
          id="calls-filter-date-range"
          className={ui.control}
          value={dateRangePreset}
          onChange={e => onDateRangePresetChange(e.target.value as CallsDateRangePreset)}
        >
          {CALLS_DATE_RANGE_PRESETS.map(p => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
      </div>
      {dateRangePreset === 'custom' && (
        <>
          <div>
            <label className={ui.label} htmlFor="calls-filter-from">From</label>
            <input
              id="calls-filter-from"
              type="date"
              className={ui.control}
              value={customFrom}
              max={customTo || undefined}
              onChange={e => onCustomFromChange(e.target.value)}
            />
          </div>
          <div>
            <label className={ui.label} htmlFor="calls-filter-to">To</label>
            <input
              id="calls-filter-to"
              type="date"
              className={ui.control}
              value={customTo}
              // FR-4.7 — constrains the To field's minimum to From client-side, as
              // defense-in-depth alongside the server's authoritative `to < from` 400.
              min={customFrom || undefined}
              onChange={e => onCustomToChange(e.target.value)}
            />
          </div>
        </>
      )}
      <div>
        <label className={ui.label} htmlFor="calls-filter-agent">Agent</label>
        <select
          id="calls-filter-agent"
          className={ui.control}
          value={agentSelection}
          onChange={e => onAgentSelectionChange(e.target.value)}
        >
          <option value="">All Agents</option>
          <option value="unassigned">Unassigned</option>
          {/* OQ-6 — every agent, active and inactive, since a historical call can reference an
              agent who has since been deactivated; inactive agents are labelled so the
              distinction is visible without a second control. */}
          {sortedAgents.map(a => (
            <option key={a.AgentID} value={a.AgentID}>
              {(a.DisplayName ?? a.AzureADObjectID ?? `Agent #${a.AgentID}`) + (a.IsActive ? '' : ' (inactive)')}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={ui.label} htmlFor="calls-filter-account">Account</label>
        <input
          id="calls-filter-account"
          type="text"
          className={ui.control}
          placeholder="Account number"
          value={accountText}
          onChange={e => setAccountText(e.target.value)}
        />
      </div>
      <div>
        <label className={ui.label} htmlFor="calls-filter-status">Status</label>
        <select
          id="calls-filter-status"
          className={ui.control}
          value={status}
          onChange={e => onStatusChange(e.target.value)}
        >
          <option value="">All Statuses</option>
          {statusOptions.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      <button type="button" className={ui.btnSecondary} onClick={onClearFilters}>
        Clear filters
      </button>
    </div>
  );
};
